import React, { useState, useEffect } from 'react';
import { getRejectionReasonDescription, fetchRejectionReasons, findRejectionReasonByContent } from '../config/api';

const StatusBadge = ({ status, validationStatus, subcategory, customerType, batchNumber, claimNumber, rejectionReasonDetail, rejectionReasonId, rejectionReason, rejectionDetails, rejectionNotes, validationErrors, ...otherProps }) => {
  const [rejectionDescription, setRejectionDescription] = useState(null);
  const [loadingDescription, setLoadingDescription] = useState(false);

  // Debug logging for rejected chargebacks
  useEffect(() => {
    if (status === 'REJECTED' || status === 'IN_ERROR') {
      console.log('🔍 Rejected/Error chargeback details:', {
        status,
        rejectionReasonDetail,
        rejectionReasonId,
        rejectionReason,
        rejectionDetails,
        rejectionNotes,
        validationErrors,
        subcategory,
        customerType,
        description: rejectionReasonDetail?.description,
        hasDescription: !!rejectionReasonDetail?.description,
        rejectionReasonKeys: rejectionReasonDetail ? Object.keys(rejectionReasonDetail) : 'null'
      });
    }
  }, [status, rejectionReasonDetail, rejectionReasonId, rejectionReason, rejectionDetails, rejectionNotes, validationErrors]);

  // Fetch rejection reason description if we have a rejection reason ID but no description
  useEffect(() => {
    const fetchDescription = async () => {
      if ((status === 'REJECTED' || status === 'IN_ERROR') && !rejectionReasonDetail?.description && !rejectionDescription) {
        setLoadingDescription(true);
        try {
          let description = null;
          
          // First try: if we have a rejection reason ID
          if (rejectionReasonId) {
            description = await getRejectionReasonDescription(rejectionReasonId);
          }
          
          // Second try: search by content if we have validation errors or other text
          if (!description && validationErrors && validationErrors.length > 0) {
            const searchText = validationErrors.join(' ');
            description = await findRejectionReasonByContent(searchText);
          }
          
          // Third try: search by rejection reason text if available
          if (!description && rejectionReason) {
            description = await findRejectionReasonByContent(rejectionReason);
          }
          
          if (description) {
            setRejectionDescription(description);
            console.log('✅ Fetched rejection description:', description);
          } else {
            console.log('❌ No rejection description found');
          }
        } catch (error) {
          console.error('❌ Error fetching rejection description:', error);
        } finally {
          setLoadingDescription(false);
        }
      }
    };

    fetchDescription();
  }, [status, rejectionReasonId, rejectionReasonDetail, validationErrors, rejectionReason]);

  // Get the description to display
  const getDescriptionToShow = () => {
    if (status === 'REJECTED' || status === 'IN_ERROR') {
      // Priority: rejectionReasonDetail.description > rejectionReason > rejectionDetails > rejectionNotes > validationErrors > fetched description > null
      return rejectionReasonDetail?.description || 
             rejectionReason || 
             rejectionDetails || 
             rejectionNotes || 
             (validationErrors && validationErrors.length > 0 ? validationErrors.join(', ') : null) ||
             rejectionDescription?.description || 
             null;
    }
    return null;
  };

  const descriptionToShow = getDescriptionToShow();

  // Get the subcategory to display
  const getSubcategoryToShow = () => {
    if (status === 'REJECTED' || status === 'IN_ERROR') {
      // Priority: rejectionReasonDetail.subcategory > validationErrors (formatted) > rejectionReason > subcategory > customerType
      if (rejectionReasonDetail?.subcategory) {
        return rejectionReasonDetail.subcategory;
      }
      
      // Format validation errors into a readable subcategory (only for REJECTED/IN_ERROR)
      if (validationErrors && validationErrors.length > 0) {
        const formattedErrors = validationErrors.map(error => {
          // Convert "Price validation failed" to "Price Validation Error"
          return error.replace(/failed$/i, 'Error').replace(/\b\w/g, l => l.toUpperCase());
        });
        return formattedErrors.join(', ');
      }
      
      return rejectionReason || subcategory || customerType;
    }
    return subcategory || customerType;
  };

  const displaySubcategory = getSubcategoryToShow();

  const getStatusInfo = () => {
    switch (status) {
      case 'IMPORTED':
        return { color: 'blue', text: 'Imported', icon: '📥' };
      case 'VALIDATED':
        return { color: 'green', text: 'Validated', icon: '✓' };
      case 'IN_ERROR':
        return { color: 'red', text: 'Has Errors', icon: '⚠' };
      case 'REJECTED':
        return { color: 'red', text: 'Rejected', icon: '✗' };
      case 'PENDING':
        return { color: 'yellow', text: 'Pending', icon: '⏳' };
      case 'APPROVED':
        return { color: 'blue', text: 'Approved', icon: '✓' };
      case 'POSTED':
        return { color: 'purple', text: 'Posted', icon: '📤' };
      case 'SUPERSEDED':
        return { color: 'purple', text: 'Superseded', icon: '↻' };
      case 'CANCELLED':
        return { color: 'gray', text: 'Cancelled', icon: '⊘' };
      default:
        return { color: 'gray', text: status, icon: '?' };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="status-badge-container">
      <span className={`status-badge status-badge-${statusInfo.color}`}>
        <span className="status-icon">{statusInfo.icon}</span>
        <span className="status-text">{statusInfo.text}</span>
      </span>
      {displaySubcategory && (
        <div className="status-subcategory">
          <span className="subcategory-text">{displaySubcategory}</span>
          {(status === 'REJECTED' || status === 'IN_ERROR') && (
            loadingDescription ? (
              <div className="debug-info"><div className="loading-description-text">Loading description...</div></div>
            ) : (descriptionToShow && descriptionToShow.trim().toLowerCase() !== String(displaySubcategory || '').trim().toLowerCase()) ? (
              // Only show the description when it adds something beyond the subcategory
              // line above — for validation errors the two are identical, so this
              // de-dupes the reason instead of printing it twice.
              <div className="debug-info"><div className="rejection-description-text">{descriptionToShow}</div></div>
            ) : null
          )}
          {status === 'REJECTED' || status === 'IN_ERROR' ? (
            rejectionReasonDetail?.title && rejectionReasonDetail.title !== displaySubcategory && (
              <div className="rejection-reason-text">{rejectionReasonDetail.title}</div>
            )
          ) : null}
        </div>
      )}
    </div>
  );
};

export default StatusBadge;
